/**
 * Theme (light / dark / system) management for the admin console.
 *
 * `ThemeProvider` is mounted once near the root (App.tsx). It:
 *   - resolves the stored choice (light, dark or system; system when nothing
 *     is stored);
 *   - toggles the `dark` class on `document.documentElement` so Tailwind v4
 *     `dark:` variants (class strategy, see index.css) and the `:root.dark`
 *     token overrides apply across surfaces;
 *   - for `system`, follows `prefers-color-scheme` live;
 *   - persists the choice to localStorage under THEME_STORAGE_KEY.
 *
 * index.html applies the same rule inline before first paint, so the class is
 * already right when React mounts — this provider only keeps it in sync.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react"

import { DEFAULT_THEME, THEME, THEME_STORAGE_KEY, type Theme } from "@/constants/theme"

interface ThemeContextValue {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const DARK_QUERY = "(prefers-color-scheme: dark)"

function readStoredTheme(): Theme {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY)
    return raw === THEME.light || raw === THEME.dark || raw === THEME.system ? raw : DEFAULT_THEME
  } catch {
    return DEFAULT_THEME
  }
}

function systemPrefersDark(): boolean {
  return typeof window !== "undefined" && !!window.matchMedia?.(DARK_QUERY).matches
}

function applyTheme(theme: Theme) {
  const dark = theme === THEME.dark || (theme === THEME.system && systemPrefersDark())
  document.documentElement.classList.toggle("dark", dark)
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme)

  useEffect(() => {
    applyTheme(theme)
    if (theme !== THEME.system || !window.matchMedia) return
    const query = window.matchMedia(DARK_QUERY)
    const onChange = () => applyTheme(theme)
    query.addEventListener("change", onChange)
    return () => query.removeEventListener("change", onChange)
  }, [theme])

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next)
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      // Persistence is best-effort; the in-memory state still drives the UI.
    }
  }, [])

  return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error("useTheme must be used within <ThemeProvider>")
  return ctx
}
